import React, { useState } from 'react';
import CommonModal from 'src/components/CommonModal';
import InputTextArea from 'src/components/InputTextArea';
import { logError } from 'src/services/LoggingService';
import { cancelOrder } from 'src/services/OrderService';
import WalToast from 'src/utils/WalToast';

interface IProps {
    handleClose: (status: boolean) => void;
    orderId: string;
    onSuccess?: (cancelRequest: any, activity?: any) => void;
}

const CANCELLATION_REASON_MAX_LENGTH = 500;

const CancelOrderModal = ({ handleClose, orderId, onSuccess }: IProps) => {
    const [cancellationReason, setCancellationReason] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const handleSubmit = async () => {
        setIsLoading(true);

        try {
            const response = await cancelOrder(orderId, cancellationReason.trim());
            WalToast.success('Cancel request submitted');
            // Axios interceptor returns response.data for 200, full response for 201 — handle both
            onSuccess?.(response?.data?.data?.request ?? response?.data?.request, response?.data?.data?.activity ?? response?.data?.activity);
            handleClose(false);
        } catch (err: any) {
            await logError(err, {
                errorMessage: err?.message || 'Error while submitting cancel request',
                errorLocation: 'CancelOrderModal: handleSubmit',
                storeInDatabase: true,
                logToConsole: true,
            });
            WalToast.error(err?.response?.data?.message || 'Cancel request failed');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <CommonModal
            title="Cancel Order Request"
            handleClose={() => handleClose(false)}
            size="max-w-[688px]"
            isOverflow={true}
            modalClassName=""
            secondaryBtnOnClick={() => handleClose(false)}
            secondaryBtnText="Cancel"
            secondaryBtnDisabled={isLoading}
            primaryBtnText="Send Request"
            primaryBtnOnClick={handleSubmit}
            primaryBtnLoading={isLoading}
            primaryBtnDisabled={isLoading || !cancellationReason.trim()}
        >
            <div className="p-4 space-y-2">
                <InputTextArea
                    inputName="cancellationReason"
                    label="Describe reason for cancellation"
                    placeholder="Add a reason for cancellation"
                    value={cancellationReason}
                    onChangeFunc={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                        const { value } = e.target;
                        if (value !== '' && value.trim() === '') return;
                        setCancellationReason(value);
                    }}
                    labelClassName="block mb-1.5"
                    className="h-[208px]"
                    maxLength={CANCELLATION_REASON_MAX_LENGTH}
                    labelWrapClass="!flex justify-between items-center"
                    labelIconRight={
                        <span className="text-gray500 text-xs leading-[18px] font-normal">
                            {cancellationReason.length}/{CANCELLATION_REASON_MAX_LENGTH}
                        </span>
                    }
                />
            </div>
        </CommonModal>
    );
};

export default CancelOrderModal;
